import rdf, { isBlankNode, isTerm, PlainFactory, } from '@ontologies/core';
const factory = new PlainFactory();
/**
 * Generate a path?query combination for the given {action} and {payload}.
 *
 * @see {createActionIRI}
 * @see {createActionPair}
 *
 * @param action The path of the action.
 * @param payload
 */
export function actionIRI(action, payload) {
    const query = Object
        .entries(payload)
        .map(([k, v]) => [
        k,
        encodeURIComponent(isTerm(v) ? factory.toNQ(v) : v),
    ]);
    return `${action}?${new URLSearchParams(query).toString()}`;
}
/**
 * Create an action generator to easy in IRI generation.
 *
 * @see {actionIRI}
 * @see {createActionPair}
 * @param base - The base Namespace to which the action/payload is added.
 */
export const createActionIRI = (base) => (action, payload) => base(actionIRI(action, payload));
/**
 * Create a action creator with the store and namespace already bound.
 *
 * @param base - The namespace to which the action name should be appended.
 * @param store - The store where the action should be dispatched.
 *
 * @see {createActionPair}
 *
 * @example
 *   const dispatch = createActionIRI(store, myNamespace);
 *   store.actions.example = {
 *     initialize: (iri: NamedNode) => dispatch('initialize', { iri })
 *   };
 */
export const createActionNS = (base, store) => {
    const actionNS = createActionIRI(base);
    return (action, payload) => store.exec(actionNS(action, payload));
};
/**
 * Create a dispatch-parse pair for use in middleware.
 *
 * @param base - The namespace to create actions on.
 * @param store - The store to dispatch actions on.
 */
export const createActionPair = (base, store) => ({
    dispatch: createActionNS(base, store),
    parse: (action) => parseAction(action),
});
/**
 * Parse an action for its base IRI and parameters separated.
 *
 * Only the last value of each param is included, so duplicates are omitted.
 *
 * The param values are parsed and converted for n-quads syntax, otherwise their literal value is
 * used. The resulting {return.params} is not guaranteed to be correct with the given {ParamMap}, it
 * is for typing purposes only.
 */
export const parseAction = (action) => {
    if (isBlankNode(action)) {
        return {
            action,
            base: action,
            params: {},
        };
    }
    const url = new URL(action.value);
    const params = {};
    url.searchParams.forEach((value, key) => {
        const t = decodeURIComponent(value);
        try {
            const parsedValue = rdf.termFromNQ(t);
            params[key] = (parsedValue !== null && parsedValue !== void 0 ? parsedValue : t);
        }
        catch (e) {
            params[key] = t;
        }
    });
    url.search = "";
    return {
        action,
        base: rdf.namedNode(url.toString()),
        params: params,
    };
};
